<script>
import ErrorAlert from "@/components/errorAlert";
import {convertToSQLDate} from "@/state/helpers";
import {cloneDeep} from "lodash";


/**
 * Form Layouts component
 */
export default {
  props: {
    campaign: {
      type: Object,
      default: () => {
        return {
          internal_name: '',
          public_name: '',
          description: '',
          start_date: '',
          end_date: '',
          color: '#3F51B5',
          hashtag_identifier: ''
        }
      }
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {ErrorAlert,},
  methods: {
    reloadFormData() {
      this.formData = cloneDeep(this.campaign);
      this.formData.start_date = convertToSQLDate(this.formData.start_date)
      this.formData.end_date = convertToSQLDate(this.formData.end_date)
    },
    submitForm() {
      this.$emit('campaignFormSubmitted', this.formData);
    },
    cancel() {
      if (this.isEdit) {
        this.reloadFormData();
      } else {
        this.$router.back()
      }
    }
  },
  data() {
    return {
      COLORS: {
        orange: "#FF5722",
        pink: "#e067ea",
        yellow: "#faf15d",
        amber: "#FFC107",
        lime: "#8BC34A",
        teal: "#009688",
        blue: "#2196F3",
        indigo: "#3F51B5",
      },
      formData: {},
    };
  },
  beforeMount() {
    this.reloadFormData()
  },
};
</script>

<template>
  <div>
    <ErrorAlert></ErrorAlert>

    <b-form @submit.prevent="submitForm()">

      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>
              Internal name
              <i class="fas fa-info-circle"
                 v-b-tooltip.hover
                 data-placement="top"
                 title="This is for your reference only. This field will not be displayd to influencers."></i>
            </label>
            <b-form-input v-model="formData.internal_name" required id="internal-name-input" type="text"></b-form-input>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label>
              Public name
              <i class="fas fa-info-circle"
                 v-b-tooltip.hover
                 data-placement="top"
                 title="This is what influencers will see in their dashboards. You might have 2 campaigns for the same product - Nike v1 and Nike v2, but you'll want influencers to see Nike summer campaign."></i>
            </label>
            <b-form-input v-model="formData.public_name" id="public-name-input" type="text"></b-form-input>
          </div>
        </div>
      </div>

      <b-form-group label="Description" label-for="description-textarea">
        <b-form-textarea v-model="formData.description" required id="description-textarea" placeholder="Describe the content you'd like from our influencers."></b-form-textarea>
      </b-form-group>

      <div class="row">

        <div class="col-md-12">
          <div class="form-group">

            <div class="row">
              <div class="col-md-6">
                <label>Start date</label>

                <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'long', day: '2-digit' }"
                                   v-model="formData.start_date" class="mb-2"></b-form-datepicker>
              </div>
              <div class="col-md-6">
                <label>End date</label>

                <b-form-datepicker :date-format-options="{ year: 'numeric', month: 'long', day: '2-digit' }"
                                   v-model="formData.end_date" class="mb-2"></b-form-datepicker>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>
          Hashtag identifier
          <i class="fas fa-info-circle"
             v-b-tooltip.hover
             data-placement="top"
             title="We will use this tag to generate unique hashtags. Influencers will add that hashtag so we can identify posts for this campaign. You can always add other hashtags to be included in the post in your Draft post section."></i>
        </label>
        <div v-if="isEdit" class="mt-1">
          <strong>{{formData.hashtag_identifier}}</strong>
        </div>
        <b-form-input v-else v-model="formData.hashtag_identifier" placeholder="#CampaignTag" required
                      id="hashtag-identifier-input" type="text"></b-form-input>
        <span v-if="!isEdit" class="text-muted">Hashtag identifier should start with '#'</span>
      </div>

      <b-form-group label="Color" label-for="color-input">
        <div class="text-center">
          <label v-for="(color, colorName) in COLORS" :key="colorName" :class="colorName">
            <input type="radio" :name="colorName" :value="color" v-model="formData.color">
            <div class="layer"></div>
            <div class="button">
              <span v-if="formData.color === color">&check;</span>
              <span v-else></span>
            </div>
          </label>
        </div>
      </b-form-group>



      <div class="text-right">
        <b-button type="button" variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button type="submit" variant="primary" class="ml-2" >Save</b-button>
      </div>
    </b-form>
  </div>
</template>
<style scoped>

input[type='radio'] {
  display: none;
}

.button {
  display: inline-block;
  position: relative;
  width: 50px;
  height: 50px;
  margin: 10px;
  cursor: pointer;
}

.button span {
  position: absolute;
  width: 50px;
  height: 50px;
  padding: 0;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  -o-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  border-radius: 100%;
  background: #eeeeee;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.26);
  transition: ease .3s;

  display: flex; /* or inline-flex */
  align-items: center;
  justify-content: center;

  color: white;
  font-size: 24px
}

.button span:hover {
  padding: 10px;
}

.orange .button span {
  background: #FF5722;
}

.pink .button span {
  background: #e067ea;
}

.yellow .button span {
  background: #faf15d;
}

.amber .button span {
  background: #FFC107;
}

.lime .button span {
  background: #8BC34A;
}

.teal .button span {
  background: #009688;
}

.blue .button span {
  background: #2196F3;
}

.indigo .button span {
  background: #3F51B5;
}

</style>
